import { useEffect, useState } from 'react';

/**
 * A portal host that keeps the donor token scope (CON-11).
 *
 * `data-cs-position` is how a document that hosts a surface from a DIFFERENT
 * position gives that subtree its own value set (CON-42, `build-scope.mjs`).
 * The dashboard is the case it exists for: it mounts EmbedForm/EventForm under
 * `[data-cs-position='donor']` inside its own document, so `--cs-text-primary`
 * inside the widget resolves to the donor ink rather than the dashboard's.
 *
 * A node portalled to `document.body` is not inside that subtree, so it
 * silently falls back to the host document's values — the menu, tooltip or
 * popover paints dashboard colours inside a donor surface. Today that is
 * invisible only because the escaping components hardcode their colours; it
 * becomes a live defect the moment one of them reads a token.
 *
 * So: portal to a host that carries the anchor's own position instead of to
 * `document.body` directly. Inside a donor drawer, that host is also appended
 * to the drawer's viewport-level dialog shell. The shell keeps it inside the
 * `aria-modal` subtree without putting it below the transformed, scrolling
 * `Drawer.Content` that would clip or offset fixed-position menus. Outside a
 * donor drawer, the host still appends to `document.body`.
 *
 * It is deliberately style-free — no class, no inline style — so it creates no
 * containing block. `position: fixed` inside it still resolves against the
 * viewport, which is what the callers rely on.
 *
 * @param {{ current: Element | null }} anchorRef element inside the scoped subtree
 * @param {boolean} isOpen                        create while true, remove when false
 * @returns {HTMLElement | null}                  pass as the portal container
 */
export function useScopedPortalHost(anchorRef, isOpen) {
  const [host, setHost] = useState(null);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      setHost(null);
      return undefined;
    }

    const anchor = anchorRef?.current;
    const node = createHostElement(anchor);
    portalParentFor(anchor).appendChild(node);
    setHost(node);

    return () => {
      node.remove();
      setHost(null);
    };
  }, [isOpen, anchorRef]);

  return host;
}

/**
 * Imperative form, for a consumer that needs the element before React renders
 * (react-select wants `menuPortalTarget` as a prop, not from a ref callback).
 * The caller owns removal.
 */
export function createScopedPortalHost(anchorEl) {
  if (typeof document === 'undefined') return null;
  const node = createHostElement(anchorEl);
  portalParentFor(anchorEl).appendChild(node);
  return node;
}

/**
 * The one place a host element is built, so both exports carry the same
 * marking. Detached — the caller appends it.
 */
function createHostElement(anchorEl) {
  const node = document.createElement('div');
  node.setAttribute('data-cs-portal-host', '');
  node.style.pointerEvents = 'auto';
  if (anchorEl?.closest?.(DIALOG_SELECTOR)) keepInAccessibilityTree(node);
  applyPositionFrom(anchorEl, node);
  return node;
}

const DRAWER_DIALOG_SELECTOR = '[data-cs-dialog-portal-root]';

function portalParentFor(anchorEl) {
  return anchorEl?.closest?.(DRAWER_DIALOG_SELECTOR) || document.body;
}

/**
 * Radix's dialog content, which is what vaul renders and what runs the sweep.
 * `alertdialog` is included because Radix's AlertDialog is the same content
 * primitive with a different role.
 *
 * Donor drawers now expose their modal shell separately through
 * `data-cs-dialog-portal-root`; this broader selector remains only for other
 * Radix dialogs whose hosts still have to append to `document.body`.
 */
const DIALOG_SELECTOR = '[role="dialog"],[role="alertdialog"]';

/**
 * Keep a host in the accessibility tree while a Radix dialog is open (CON-62).
 *
 * Donor-drawer hosts now live in the drawer's modal shell, but they remain a
 * sibling of Radix's Drawer.Content. `hideOthers(content)` recursively sweeps
 * that sibling even though their shared shell is retained, so the exemption is
 * still required for `aria-hidden`; the new ancestry solves the separate
 * `aria-modal` boundary. It is applied ONLY when the anchor is inside a dialog,
 * which is the whole claim the exemption rests on. A host anchored in ordinary
 * page content is page content, and page content behind a modal SHOULD be hidden
 * — exempting it unconditionally would announce the page behind the drawer, the
 * opposite defect.
 * The anchor is inside the dialog's DOM by the time either export runs (a ref
 * callback fires after its subtree is inserted; the hook runs in an effect), and
 * the two tests that mount a real drawer are what hold that ordering.
 *
 * Every panel these hosts open inside is a Radix dialog underneath —
 * `ResponsiveDrawer` and `OverlayElement` both build on vaul, which builds on
 * `@radix-ui/react-dialog`. `DialogContentModal` calls `hideOthers(content)` from
 * the `aria-hidden` package in a mount effect: it walks `document.body`'s
 * children and sets `aria-hidden="true"` on every one that does not contain the
 * dialog. A host appended to `document.body` is exactly such a child, so the
 * menu it carries left the accessibility tree — and `aria-hidden` is INHERITED
 * and cannot be overridden from inside, so nothing the portalled content does
 * can recover it. A screen-reader donor met a required question with nothing to
 * hear. Measured on the widget's phone drawer and on the checkout's second open.
 *
 * `hideOthers` keeps three kinds of node: the dialog's own subtree, `script`
 * elements, and anything carrying `aria-live` — the last so a live region can
 * still speak from behind a dialog. This host asks for that exemption, and it is
 * entitled to it: the content it holds belongs to a control the dialog itself
 * renders, so it IS inside the dialog logically and is only outside structurally
 * because a body-level portal is what lets a menu escape a scrolling, translated
 * ancestor. Everything genuinely behind the dialog stays hidden — this exempts
 * the menu, it does not defeat the mechanism.
 *
 * `off` is ARIA's default assertiveness, so this announces nothing an unmarked
 * element would not; the attribute is here for the exemption alone.
 *
 * NOT a one-shot clear, which is the shape that looks right and is not: React
 * runs a child's effects BEFORE its parent's, so a `removeAttribute` when the
 * dropdown mounts happens first and `hideOthers` overwrites it (measured).
 *
 * This leans on `aria-hidden`'s exemption list, so it is guarded by a test that
 * mounts a real drawer and reads the outcome rather than by this comment:
 * `donation-checkout/src/Components/ui/__characterization__/multiSelectDropdownCursor.test.jsx`.
 */
function keepInAccessibilityTree(node) {
  node.setAttribute('aria-live', 'off');
}

function applyPositionFrom(anchorEl, node) {
  const scope = anchorEl?.closest?.('[data-cs-position]');
  const position = scope?.getAttribute('data-cs-position');
  if (position) node.setAttribute('data-cs-position', position);
}
